import userManager from '../services/oidcUserManager';

/**
 * Obtém o usuário logado
 * @returns {Promise<Object>} Objeto do usuário com claims (email, preferred_username, etc)
 */
export async function getLoggedInUser() {
  return await userManager.getUser();
}

/**
 * Extrai o domínio do email do usuário logado
 * Ex: "usuario@globo.com" → "globo.com"
 * @returns {Promise<string|null>} Domínio do email ou null se não encontrado
 */
export async function getEmailDomain() {
  const email = await getEmail();
  if (!email) return null;
  return email.split('@')[1] || null;
}

/**
 * Obtém o email completo do usuário logado
 * @returns {Promise<string|null>} Email do usuário ou null se não encontrado
 */
export async function getEmail() {
  // TODO: Teste com domínio fake para desenvolvimento local sem autenticação.
  // const TEST_EMAIL = 'teste@frm.org';
  // console.warn('[TEST] getEmail() retornando email fake:', TEST_EMAIL);
  // return TEST_EMAIL;

  const user = await getLoggedInUser();
  return user?.profile?.email || null;
}

/**
 * Obtém o nome do usuário logado
 * @returns {Promise<string|null>} Nome do usuário ou null se não encontrado
 */
export async function getUserName() {
  const user = await getLoggedInUser();
  return user?.profile?.name || user?.profile?.preferred_username || null;
}

/**
 * Verifica se o email pertence a um domínio específico
 * @param {string} domain - Domínio a verificar (ex: "globo.com")
 * @returns {Promise<boolean>} true se o email é do domínio especificado
 */
export async function isEmailFromDomain(domain) {
  const emailDomain = await getEmailDomain();
  return emailDomain?.toLowerCase() === domain.toLowerCase();
}

const TLD_PARTS = new Set(['com', 'net', 'org', 'br', 'io', 'gov', 'edu', 'co']);
const EXTCOLAB_DOMAIN = 'extcolab.globo';
const BUSINESS_CLIENT_SELECTOR_DOMAINS = new Set(['globo.com', 'g.globo', 'prestador.globo']);

/**
 * Indica se o usuário pode escolher business_client manualmente na interface.
 * @returns {Promise<boolean>} true para domínios administrativos permitidos
 */
export async function canChooseBusinessClient() {
  const domain = await getEmailDomain();
  return domain ? BUSINESS_CLIENT_SELECTOR_DOMAINS.has(domain.toLowerCase()) : false;
}

function extractExtColabBusinessClient(email) {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  const match = normalizedEmail.match(/_([a-z0-9-]+)\.(?:org|com|net)(?:\.[a-z]{2})?@extcolab\.globo$/);

  return match?.[1] || null;
}

/**
 * Obtém o domínio a ser exibido na interface para o usuário logado.
 * Ex: "usuario@globo.com" → "globo.com"
 *     "renan.silva_frm.org.br@extcolab.globo" → "frm"
 * @returns {Promise<string|null>} Domínio exibível ou null
 */
export async function getDisplayEmailDomain() {
  const email = await getEmail();
  if (!email) return null;

  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return null;

  if (domain === EXTCOLAB_DOMAIN) {
    return extractExtColabBusinessClient(email);
  }

  return domain;
}

/**
 * Extrai o business client baseado no domínio do email do usuário logado.
 * Remove sufixos TLD e retorna o núcleo do domínio.
 * Ex: "usuario@globo.com"           → "globo"
 *     "usuario@g.globo.com"         → "globo"
 *     "usuario@prestador.globo.com" → "globo"
 *     "usuario@eletromidia.com.br"  → "eletromidia"
 *     "renan.silva_frm.org.br@extcolab.globo" → "frm"
 * @returns {Promise<string|null>} Business client normalizado ou null
 */
export async function getBusinessClient() {
  const email = await getEmail();
  if (!email) return null;

  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return null;

  if (domain === EXTCOLAB_DOMAIN) {
    return extractExtColabBusinessClient(email);
  }

  const parts = domain.split('.');
  const meaningful = parts.filter(p => !TLD_PARTS.has(p));

  return meaningful.length > 0 ? meaningful[meaningful.length - 1] : parts[0];
}
